export * from "./codegen";

import {
  ApolloLink,
  InMemoryCache,
  Observable,
  HttpLink,
  CombinedGraphQLErrors,
  ServerError,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { Cookies } from "react-cookie";
import { ErrorLink } from "@apollo/client/link/error";
import * as jose from "jose";
import {
  createApolloLoaderHandler,
  ApolloClient,
} from "@apollo/client-integration-react-router";
const cookies = new Cookies();

let graphqlUrl = "";
let revision = "";
if (typeof window === "undefined") {
  // SSR (in-container) prefers a loopback URL pointing at the Hono backend
  // running in the same Cloud Run instance, skipping Cloudflare + GCP LB +
  // dispatcher hops. Falls back to the public URL when GRAPHQL_API_LOCALHOST
  // is unset (local dev, where the localhost var is not configured).
  graphqlUrl =
    (process.env.GRAPHQL_API_LOCALHOST as string | undefined) ??
    (process.env.GRAPHQL_API as string);
  revision = process.env.REVISION as string;
} else {
  graphqlUrl = webConfig.graphqlAPI;
  revision = webConfig.revision;
}

const httpLink = new HttpLink({
  uri: graphqlUrl,
});

let endpointLink = httpLink;
if (typeof window !== "undefined") {
  const wsLink = new GraphQLWsLink(
    createClient({
      url: graphqlUrl.replaceAll("https:", "wss:"),
      connectionParams: () => {
        return refreshIfCloseToExpiration(cookies)
          .then((token) => ({
            authentication:
              "Bearer " + (token || cookies.get("x-ms-access-token") || ""),
          }))
          .catch((error) => {
            // This use is probably not authenticated, continue without auth.
            return {};
          });
      },
    }),
  );

  endpointLink = ApolloLink.split(
    ({ query }) => {
      if (typeof window === "undefined") {
        return false;
      }
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    httpLink,
  );
}

const refreshTokenLink = new ApolloLink((operation, forward) => {
  if (typeof window !== "undefined") {
    return new Observable((observer) => {
      refreshIfCloseToExpiration(cookies)
        .then((token) => {
          // Store the refreshed token in operation context
          operation.setContext(({ headers = {} }) => ({
            headers,
            refreshedToken: token,
          }));
        })
        .catch((error) => {
          // This user is probably not authenticated, continue without auth.
        })
        .finally(() => {
          // Forward the operation after token refresh attempt
          const subscription = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });

          // Return cleanup function
          return () => subscription.unsubscribe();
        });
    });
  }
  return forward(operation);
});

const metadataHeadersLink = setContext((_, { headers }) => {
  let newHeaders = {
    ...headers,
    "x-nspr-app": "web",
    "x-nspr-app-version": revision,
  };
  return { headers: newHeaders };
});

const authLink = setContext((operation, context) => {
  if (typeof window === "undefined") {
    // In the backend, headers are added in the query directly, but let's add the app/revision
    return {
      headers: {
        ...context.headers,
      },
    };
  }

  // Get the refreshed token from context if available
  const token =
    context.refreshedToken || cookies.get("x-ms-access-token") || "";

  return {
    headers: {
      ...context.headers,
      Authorization: "Bearer " + token,
    },
  };
});

const errorLink = new ErrorLink(({ error, result }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  } else if (ServerError.is(error)) {
    console.log(`[Server error]: ${error.message}`);
  } else if (error) {
    console.log(`[Other error]: ${error.message}`);
  }
});

const directionalLink = new ApolloLink((operation, forward) => {
  return forward(operation);
}).split(
  (operation) => !!operation.getContext().skipAuth,
  metadataHeadersLink.concat(errorLink).concat(endpointLink),
  metadataHeadersLink
    .concat(errorLink)
    .concat(refreshTokenLink)
    .concat(authLink)
    .concat(endpointLink),
);
const cache: InMemoryCache = new InMemoryCache();
export const getServerClient = (request?: Request) => {
  return new ApolloClient({
    cache,

    defaultOptions: {
      query: {
        fetchPolicy: "no-cache",
        errorPolicy: "all", // Return errors in response instead of throwing
      },
    },

    link: directionalLink,
  });
};
export const apolloLoader = createApolloLoaderHandler(getServerClient);

function refreshToken() {
  return fetch("/api/oauth/access_token")
    .then(async (response) => {
      const isJson = response.headers
        .get("content-type")
        ?.includes("application/json");
      const data = isJson ? await response.json() : null;

      // check for error response
      if (!response.ok) {
        // get error message from body or default to response status
        const error = (data && data.message) || response.status;
        return Promise.reject(error);
      }

      return Promise.resolve(data.AccessToken);
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

export async function refreshIfCloseToExpiration(cookies: Cookies) {
  let loggedIn = cookies.get("x-ms-logged-in");
  if (!loggedIn) {
    return;
  }
  let tokenValue = cookies.get("x-ms-access-token");
  if (!tokenValue) {
    return refreshToken();
  }
  const claims = jose.decodeJwt(tokenValue);
  if (
    claims?.exp &&
    new Date(claims.exp * 1000).getTime() > new Date().getTime() + 20000 // Token still live for 20s at least
  ) {
    return Promise.resolve();
  }
  // It has expired, let's refresh
  return refreshToken();
}
